import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptConnection,
  createConnection,
  deleteConnection,
  getMyConnections,
  leaveGroup,
  patchConnectionType,
  rejectConnection,
} from '../services/connectionService';
import type {
  ConnectionSegment,
  CreateConnectionRequest,
  UserSettableConnectionType,
} from '../types/connection';

/**
 * Lists the caller's connections for a segment. Pass `enabled: false` to
 * defer the fetch (e.g. until a tab is opened).
 */
export function useMyConnections(
  segment: ConnectionSegment = 'all',
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['connections', segment],
    queryFn: () => getMyConnections(segment),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Sends a connection request (email-driven). On success, invalidates every
 * connections list so the outgoing-requests tab reflects the new pending row.
 */
export function useCreateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateConnectionRequest) => createConnection(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

/**
 * Accepts a pending request. Invalidates every connections list so the row
 * moves out of `requests_to_me` and into `accepted`.
 */
export function useAcceptConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => acceptConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

/**
 * Declines a pending request. Invalidates every connections list so the row
 * drops out of `requests_to_me`.
 */
export function useRejectConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rejectConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

/**
 * Re-categorizes a connection's type (initiator-only). Invalidates every
 * connections list so the new type label shows everywhere the row appears.
 */
export function usePatchConnectionType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      type,
    }: {
      id: string;
      type: UserSettableConnectionType;
    }) => patchConnectionType(id, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

/**
 * Disconnects / removes a connection (also withdraws a pending sent request).
 * Invalidates every connections list so the row drops out wherever it showed.
 */
export function useDeleteConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

/**
 * Leaves the marketing-QR campaign that auto-created the given connection.
 * Invalidates every connections list so the group label (and, when
 * `removeConnections=true`, the removed rows) reflect immediately.
 */
export function useLeaveGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      removeConnections,
    }: {
      id: string;
      removeConnections: boolean;
    }) => leaveGroup(id, removeConnections),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}
